import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { get } from 'lodash';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { Grid } from '@material-ui/core';
import SearchContainer from 'containers/IdentificationPage/SearchContainer';
import ManagerPendingClaims from 'containers/ManagerHomePage/PendingClaims';
import PvIndexed from 'containers/ManagerHomePage/PvIndexed';
import { requestManagerPendingClaims } from 'containers/ManagerPendingClaims/actions';
import { makeSelectPendingClaims } from 'containers/ManagerPendingClaims/selectors';
import { makeSelectListingsByField } from 'containers/AppContainer/selectors';
import { LISTINGS } from 'containers/data-structure';
import { Listings, SagisConfig } from 'containers/AppContainer/typings';
import {
  requestFindClaims,
  requestSearchClaims,
} from 'containers/IdentificationPage/actions';
import userPrivileges, {
  PRIVILEGES,
} from 'containers/services/security/user-privileges';
import AssignedClaimsTable from 'containers/ManagerHomePage/AssignedClaimsTable';
import {
  requestLastAssignedClaims,
  setSearchQueryMonitoringManager,
  setSelectedResultSearch,
} from 'containers/ManagerHomePage/actions';
import { makeSelectLastClaims } from 'containers/ManagerHomePage/selectors';
import { AssignedClaim } from 'containers/CourtManagerAssignedClaims/typings';
import { makeSelectListings } from 'containers/AppContainer/selectors';
import * as utils from 'containers/RecapManagerPage/utils';
import { ITEM_KEYS, SAGIS_KEYS } from 'containers/RecapManagerPage/constants';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import currentUserStorage from 'containers/services/security/current-user-storage';
import { HOME_PAGE_ROWS_PER_PAGE } from 'constants/global';
import { SearchQuery } from 'containers/IdentificationPage/typings';
import { resetStoreClaim } from 'containers/RecapManagerPage/actions';
import { resetSearchClaimsStore } from 'containers/IdentificationPage/actions';
import { getAssignedManagerCode } from 'containers/CourtManagerAssignedClaims/utils';
import { makeSelectSearchClaims } from 'containers/IdentificationPage/selectors';
import { resetTabsIndex } from 'containers/AppContainer/actions';

export interface ManagerHomePageProps {
  requestManagerPendingClaims: Function;
  requestFindClaims: Function;
  pendingClaims: [];
  cities: SagisConfig[];
  requestLastAssignedClaims: Function;
  lastAssignedClaims: AssignedClaim[];
  listings: Listings;
  setSearchQueryMonitoringManager: Function;
  setSelectedResultSearch: Function;
  policyInfo: any;
  resetSearchClaimsStore: Function;
  resetStoreClaim: Function;
  resetTabsIndex: Function;
  requestSearchClaims: Function;
}

const Claims = styled.div`
  .claims {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
  }
  .pendingClaims {
    padding-top: 32px;
    width: 100%;
  }
`;

const StyledAssignedClaimsTable = styled(AssignedClaimsTable)`
  margin-top: 75px;
`;

const ManagerHomePage: React.FC<ManagerHomePageProps> = (
  props,
): JSX.Element => {
  const [firstSearch, setFirstSearch] = useState(false);
  useEffect(() => {
    props.resetSearchClaimsStore();
    props.resetStoreClaim();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [searchQuery, setSearchQuery] = React.useState<SearchQuery>({
    lossDate: null,
    registrationNumber: null,
    policyNumber: null,
    claimNumber: null,
    assuredCIN: null,
    assuredName: null,
    victimName: null,
    courtNumber: null,
  });

  /* istanbul ignore next */
  const submitSearch = () => {
    if (userPrivileges.userHasPrivilege(PRIVILEGES.ISOPENINGMANAGER)) {
      setFirstSearch(true);
      props.requestFindClaims({
        ...searchQuery,
        isBasicManagementActive:
          featureService.hasFeature(FEATURES.BASIC_MANAGEMENT) ||
          featureService.hasFeature(FEATURES.EVOLUTION_P3),
      });
    }
    if (
      userPrivileges.userHasPrivilege(PRIVILEGES.ISCOURTMONITORINGMANAGER) ||
      userPrivileges.userHasPrivilege(PRIVILEGES.ISCONSULTATIONMANAGER)
    ) {
      setFirstSearch(true);
      props.requestFindClaims({
        ...searchQuery,
        procedureNatureId:
          get(searchQuery, 'procedureNatureId') === 0
            ? null
            : get(searchQuery, 'procedureNatureId'),
        jurisdictionDegreeId:
          get(searchQuery, 'jurisdictionDegreeId') === 0
            ? null
            : get(searchQuery, 'jurisdictionDegreeId'),
        jurisdiction:
          get(searchQuery, 'jurisdiction') === '0'
            ? null
            : get(searchQuery, 'jurisdiction'),
        isBasicManagementActive: featureService.hasFeature(
          FEATURES.BASIC_MANAGEMENT,
        ),
      });
      props.setSearchQueryMonitoringManager(searchQuery);
    }
    if (userPrivileges.userHasPrivilege(PRIVILEGES.ISCOURTMANAGER)) {
      setFirstSearch(true);
      props.requestLastAssignedClaims({
        assignedManagerCode: getAssignedManagerCode(
          get(props.listings, LISTINGS.ASSIGNMENT_MANAGER, []),
          get(currentUserStorage.get(), 'username', ''),
        ),
        claimsCountToDisplay: HOME_PAGE_ROWS_PER_PAGE,
        searchQuery: {
          ...searchQuery,
          isInboxesActive: featureService.hasFeature(
            FEATURES.ATTRIBUTION_INBOXES_MANAGER,
          ),
          isBasicManagementActive:
            featureService.hasFeature(FEATURES.BASIC_MANAGEMENT) ||
            featureService.hasFeature(FEATURES.BASIC_MANAGEMENT_REGULATION),
          isManager: userPrivileges.userHasPrivilege(PRIVILEGES.ISMANAGER),
        },
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }
  };

  return (
    <div>
      <SearchContainer
        searchQuery={searchQuery}
        onChangeSearchQuery={setSearchQuery}
        submitSearch={submitSearch}
        isManager={true}
      />

      {userPrivileges.userHasPrivilege(PRIVILEGES.ISOPENINGMANAGER) && (
        <Claims>
          <div className="claims">
            <div className="pendingClaims">
              {!featureService.hasFeature(FEATURES.MANAGER_OPENING) &&
              !featureService.hasFeature(
                FEATURES.ATTRIBUTION_INBOXES_MANAGER,
              ) ? (
                <ManagerPendingClaims
                  claims={props.pendingClaims}
                  cities={props.cities}
                />
              ) : (
                <Grid container direction="row" spacing={1}>
                  <Grid item xs={6}>
                    {!featureService.hasFeature(
                      FEATURES.ATTRIBUTION_INBOXES_MANAGER,
                    ) && (
                      <ManagerPendingClaims
                        claims={props.pendingClaims}
                        cities={props.cities}
                      />
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    {featureService.hasFeature(FEATURES.MANAGER_OPENING) && (
                      <PvIndexed />
                    )}
                  </Grid>
                </Grid>
              )}
            </div>
          </div>
        </Claims>
      )}

      {firstSearch &&
        (userPrivileges.userHasPrivilege(PRIVILEGES.ISCOURTMANAGER) ||
          userPrivileges.userHasPrivilege(
            PRIVILEGES.ISCOURTMONITORINGMANAGER,
          ) ||
          userPrivileges.userHasPrivilege(PRIVILEGES.ISCONSULTATIONMANAGER) ||
          (userPrivileges.userHasPrivilege(PRIVILEGES.ISOPENINGMANAGER) &&
            featureService.hasFeature(
              FEATURES.ATTRIBUTION_INBOXES_MANAGER,
            ))) && (
          <>
            <StyledAssignedClaimsTable
              pendingClaims={props.pendingClaims}
              lastAssignedClaims={props.lastAssignedClaims}
              lawyers={utils.getListBySagisKey(
                get(props.listings, LISTINGS.STACKHOLDER_TYPE, []),
                get(props.listings, LISTINGS.STACKHOLDERS, []),
                ITEM_KEYS.LAWYER,
                SAGIS_KEYS.ITEM_TYPE as keyof SagisConfig,
              )}
              isCourtMonitoringManager={userPrivileges.userHasPrivilege(
                PRIVILEGES.ISCOURTMONITORINGMANAGER,
              )}
              isConsultationManager={userPrivileges.userHasPrivilege(
                PRIVILEGES.ISCONSULTATIONMANAGER,
              )}
              isOpeningManager={userPrivileges.userHasPrivilege(
                PRIVILEGES.ISOPENINGMANAGER,
              )}
              isTransactionManager={userPrivileges.userHasPrivilege(
                PRIVILEGES.ISTRANSACTIONMANAGER,
              )}
              resetTabsIndex={props.resetTabsIndex}
              setSelectedResultSearch={props.setSelectedResultSearch}
              requestSearchClaims={props.requestSearchClaims}
            ></StyledAssignedClaimsTable>
          </>
        )}
    </div>
  );
};

const mapDispatchToProps = {
  requestManagerPendingClaims,
  requestFindClaims,
  requestLastAssignedClaims,
  setSearchQueryMonitoringManager,
  setSelectedResultSearch,
  resetSearchClaimsStore,
  resetStoreClaim,
  resetTabsIndex,
  requestSearchClaims,
};

const mapStateToProps = createStructuredSelector({
  pendingClaims: makeSelectPendingClaims(),
  cities: makeSelectListingsByField(LISTINGS.CITIES),
  lastAssignedClaims: makeSelectLastClaims(),
  listings: makeSelectListings(),
  policyInfo: makeSelectSearchClaims(),
});

export default connect(mapStateToProps, mapDispatchToProps)(ManagerHomePage);
